import type { NewsStatusKey, RadarKey, SourceKey } from './api';

export const SOURCE_KEYS: SourceKey[] = ['cafe', 'blog', 'bobaedream', 'theqoo', 'blind', 'cook82', 'pann'];

export const SOURCE_LABEL: Record<SourceKey, string> = {
  cafe: '네이버 카페',
  blog: '네이버 블로그',
  bobaedream: '보배드림',
  theqoo: '더쿠',
  blind: '블라인드',
  cook82: '82쿡',
  pann: '네이트판',
};

export const SOURCE_SHORT: Record<SourceKey, string> = {
  cafe: '카페',
  blog: '블로그',
  bobaedream: '보배',
  theqoo: '더쿠',
  blind: '블라',
  cook82: '82쿡',
  pann: '네이트판',
};

export const SOURCE_COLOR: Record<SourceKey, string> = {
  cafe: '#03c75a',
  blog: '#20a35a',
  bobaedream: '#e8590c',
  theqoo: '#7048e8',
  blind: '#e03131',
  cook82: '#f08c00',
  pann: '#ff4b4b',
};

const pad = (n: number) => String(n).padStart(2, '0');

/** KST 기준 "MM/DD HH:mm" */
export function kstStamp(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 9 * 3600 * 1000);
  return `${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** 블로그는 작성일, 나머지는 작성 시각이 없어 수집 시각을 보여준다 */
export function formatSourceTime(s: { source: SourceKey; postedDate?: string; collectedAt: string }): string {
  if (s.source === 'blog' && s.postedDate) {
    const [, m, d] = s.postedDate.split('-');
    return `${m}/${d}`;
  }
  return `수집 ${kstStamp(s.collectedAt)}`;
}

const DOW = ['일', '월', '화', '수', '목', '금', '토'];

/** KST 날짜 키 (yyyy-mm-dd) */
const kstDay = (d: Date) => new Date(d.getTime() + 9 * 3600 * 1000).toISOString().slice(0, 10);

/** 회차 라벨: "오늘 11/03(화) 17:00" / "어제 …" / "11/01(일) 01:00". short면 "오늘 17:00" */
export function roundLabel(iso: string, now = new Date(), short = false): string {
  const d = new Date(iso);
  const k = new Date(d.getTime() + 9 * 3600 * 1000);
  const md = `${pad(k.getUTCMonth() + 1)}/${pad(k.getUTCDate())}`;
  const hm = `${pad(k.getUTCHours())}:${pad(k.getUTCMinutes())}`;
  const day = kstDay(d);
  const rel =
    day === kstDay(now) ? '오늘' : day === kstDay(new Date(now.getTime() - 24 * 3600 * 1000)) ? '어제' : '';
  if (short) return `${rel || md} ${hm}`;
  const date = `${md}(${DOW[k.getUTCDay()]})`;
  return rel ? `${rel} ${date} ${hm}` : `${date} ${hm}`;
}

export const RADAR_TABS: { key: RadarKey; label: string; icon: string; desc: string }[] = [
  { key: 'trend', label: '트렌드', icon: '📈', desc: '요즘 자주 언급되는 주제' },
  { key: 'community', label: '커뮤니티 이슈', icon: '🔥', desc: '보배드림·더쿠·블라인드·네이트판 인기글' },
  { key: 'life', label: '생활 의문·불만', icon: '🙋', desc: '카페·블로그에 새로 올라온 생활 속 문제' },
];

export const KIND_LABEL: Record<string, string> = {
  debate: '갑론을박',
  harm: '피해·민원',
  incident: '사건 반응',
  policy: '제도 반응',
};
export const KIND_ORDER = ['debate', 'harm', 'incident', 'policy'];

type Labels = { issue: string; implication: string; angle: string; reporting: string };
export const ANALYSIS_LABELS: Record<RadarKey, Labels> = {
  community: { issue: '무슨 이야기인가', implication: '왜 반응이 큰가', angle: '발제 각도', reporting: '취재 포인트' },
  life: { issue: '어떤 상황인가', implication: '누가 어떤 불편을 겪나', angle: '발제 각도', reporting: '확인처' },
  trend: { issue: '현상 설명', implication: '왜 지금 늘고 있나', angle: '기획 각도', reporting: '취재 포인트' },
};

/** 🆕 기사 없음 / 📰 보도 n건 / 확인 실패 */
export function newsBadge(news: { status: NewsStatusKey; count: number }): { text: string; color: string } | null {
  if (news.status === 'new') return { text: '🆕 기사 없음', color: '#389e0d' };
  if (news.status === 'some' || news.status === 'many') return { text: `📰 보도 ${news.count}건`, color: '#8c8c8c' };
  return null;
}

/** 출처 단위 이름: 카페명·블로거, 커뮤니티는 사이트 이름 */
export function venueName(s: { source: SourceKey; venue: string }): string {
  if (s.venue.startsWith('cafe:') && !s.venue.startsWith('cafe:http')) return s.venue.slice(5);
  return SOURCE_LABEL[s.source] ?? s.source;
}

/** 날짜 라벨 "오늘 11/10(화)" / "11/08(일)" */
export function dayLabel(day: string, now = new Date()): string {
  const d = new Date(`${day}T12:00:00+09:00`);
  const md = `${day.slice(5, 7)}/${day.slice(8, 10)}(${DOW[d.getUTCDay()]})`;
  if (day === kstDay(now)) return `오늘 ${md}`;
  if (day === kstDay(new Date(now.getTime() - 24 * 3600 * 1000))) return `어제 ${md}`;
  return md;
}
